import React, { useRef, useEffect } from 'react';

const SCALE = 0.18;
const s = (mm: number) => mm * SCALE;

const THICK = 100;
const PT = s(THICK);
const TD = PT * 0.5;

const DOOR_W = 800;
const L_PANEL = 150;
const R_PANEL = 150;

type EndType = 'yangsu' | 'eumsu';
type DoorType = 'right' | 'left' | 'double';
type WallSide = 'bottom' | 'top' | 'left' | 'right';

interface PanelVariant { no: number; lt: EndType; rt: EndType; }

const DOOR_TYPES: { type: DoorType; label: string }[] = [
  { type: 'right',  label: '단문-오른쪽' },
  { type: 'left',   label: '단문-왼쪽' },
  { type: 'double', label: '양문' },
];

const PANEL_VARIANTS: PanelVariant[] = [
  { no: 1, lt: 'yangsu', rt: 'eumsu' },
  { no: 2, lt: 'eumsu',  rt: 'yangsu' },
  { no: 3, lt: 'eumsu',  rt: 'eumsu' },
  { no: 4, lt: 'yangsu', rt: 'yangsu' },
];

const WALL_SIDES: { side: WallSide; label: string; angle: number }[] = [
  { side: 'top',    label: '상단벽', angle: 0 },
  { side: 'bottom', label: '하단벽', angle: Math.PI },
  { side: 'left',   label: '좌측벽', angle: -Math.PI / 2 },
  { side: 'right',  label: '우측벽', angle: -Math.PI * 1.5 },
];

const DoorTest: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const lp = s(L_PANEL);
    const dw = s(DOOR_W);
    const rp = s(R_PANEL);
    const panelW = lp + dw + rp;

    const leafRoom = dw * 0.25 + 16;
    // 수평벽(top/bottom): 패널이 가로 → 셀 높이 = PT + leafRoom 양쪽
    const hCellW = panelW + TD * 2 + 16;
    const hCellH = PT + leafRoom * 2 + 10;
    // 수직벽(left/right): 패널이 세로 → 셀 높이 = panelW + TD*2 + leafRoom 양쪽
    const vCellW = PT + leafRoom * 2 + 10;
    const vCellH = panelW + TD * 2 + 16;
    // 셀 폭은 가로벽 기준으로 통일 (세로벽도 같은 열 폭 사용)
    const cellW = Math.max(hCellW, vCellW);

    const colGap = 30;
    const variantGap = 8;
    const rowGap = 50;
    const labelColW = 52;
    const headerH = 36;

    // 벽별 셀 높이
    const getCellH = (side: WallSide) =>
      (side === 'left' || side === 'right') ? vCellH : hCellH;

    // 각 행의 colH = 4개 셀 높이 합 (벽별로 다름)
    const getColH = (side: WallSide) =>
      PANEL_VARIANTS.length * getCellH(side) + (PANEL_VARIANTS.length - 1) * variantGap;

    canvas.width  = labelColW + DOOR_TYPES.length * (cellW + colGap) + 20;
    canvas.height = headerH + WALL_SIDES.reduce((acc, ws) => acc + getColH(ws.side) + rowGap, 0) + 20;

    ctx.fillStyle = '#F0F0F0';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 타이틀
    ctx.fillStyle = '#1565C0'; ctx.font = 'bold 13px Arial';
    ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    ctx.fillText('문 48종류 (4방향 × 3타입 × 4패널)', 8, 8);

    // 열 헤더
    DOOR_TYPES.forEach((dt, ci) => {
      const cx = labelColW + ci * (cellW + colGap) + cellW / 2;
      ctx.fillStyle = '#333'; ctx.font = 'bold 11px Arial';
      ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      ctx.fillText(dt.label, cx, headerH - 16);
    });

    // --- 그리기 함수 ---
    const drawTri = (x: number, y: number, type: EndType, side: 'left' | 'right') => {
      ctx.beginPath();
      if (side === 'left') {
        if (type === 'yangsu') {
          ctx.moveTo(x, y); ctx.lineTo(x - TD, y + PT/2); ctx.lineTo(x, y + PT);
        } else {
          ctx.moveTo(x, y); ctx.lineTo(x + TD, y + PT/2); ctx.lineTo(x, y + PT);
        }
      } else {
        if (type === 'yangsu') {
          ctx.moveTo(x, y); ctx.lineTo(x + TD, y + PT/2); ctx.lineTo(x, y + PT);
        } else {
          ctx.moveTo(x, y); ctx.lineTo(x - TD, y + PT/2); ctx.lineTo(x, y + PT);
        }
      }
      ctx.closePath();
      ctx.fillStyle = '#FFF'; ctx.fill();
      ctx.strokeStyle = '#333'; ctx.lineWidth = 1.5; ctx.stroke();
    };

    const drawSidePanel = (ox: number, oy: number, pw: number, lt: EndType, rt: EndType,
                            drawLeft: boolean, drawRight: boolean) => {
      ctx.fillStyle = '#FFE0B2';
      ctx.fillRect(ox, oy, pw, PT);
      ctx.strokeStyle = '#F57C00'; ctx.lineWidth = 1.5;
      ctx.strokeRect(ox, oy, pw, PT);
      if (drawLeft)  drawTri(ox, oy, lt, 'left');
      if (drawRight) drawTri(ox + pw, oy, rt, 'right');
    };

    const drawDoorBody = (ox: number, oy: number, pw: number) => {
      ctx.fillStyle = '#C8E6C9';
      ctx.fillRect(ox, oy, pw, PT);
      ctx.strokeStyle = '#2E7D32'; ctx.lineWidth = 1.5;
      ctx.strokeRect(ox, oy, pw, PT);
      ctx.fillStyle = '#2E7D32'; ctx.font = `bold ${Math.max(8, PT * 0.3)}px Arial`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(`${DOOR_W}`, ox + pw/2, oy + PT/2);
    };

    const drawLeaf = (hx: number, hy: number, toRight: boolean, outerDir: number, leafLen: number) => {
      const ex = hx + (toRight ? leafLen : -leafLen);
      ctx.strokeStyle = '#333'; ctx.lineWidth = 1.5; ctx.setLineDash([]);
      ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(ex, hy); ctx.stroke();

      const startA = toRight ? 0 : Math.PI;
      const endA   = outerDir > 0 ? Math.PI/2 : -Math.PI/2;
      const ccw    = toRight ? outerDir < 0 : outerDir > 0;
      ctx.save(); ctx.translate(hx, hy); ctx.scale(1, 0.25);
      ctx.strokeStyle = '#333'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(0, 0, leafLen, startA, endA, ccw); ctx.stroke();
      ctx.restore();

      const arcEndY = hy + outerDir * leafLen * 0.25;
      ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(hx, arcEndY); ctx.stroke();
    };

    // 셀 하나 그리기 (로컬 좌표, 패널 수평 기준)
    const drawCell = (dt: DoorType, pv: PanelVariant) => {
      const tdPad = TD;
      const doorX = tdPad + lp;
      const outerDir = -1;
      const outerY   = 0;

      drawSidePanel(tdPad, 0, lp, pv.lt, pv.rt, true, false);
      drawDoorBody(doorX, 0, dw);
      drawSidePanel(doorX + dw, 0, rp, pv.lt, pv.rt, false, true);

      if (dt === 'right') {
        drawLeaf(doorX, outerY, true, outerDir, dw);
      } else if (dt === 'left') {
        drawLeaf(doorX + dw, outerY, false, outerDir, dw);
      } else {
        drawLeaf(doorX, outerY, true, outerDir, dw / 2);
        drawLeaf(doorX + dw, outerY, false, outerDir, dw / 2);
      }
    };

    let rowTop = headerH;
    WALL_SIDES.forEach((ws) => {
      const cellH = getCellH(ws.side);
      const colH  = getColH(ws.side);

      // 행 라벨 (중앙)
      ctx.fillStyle = '#1565C0'; ctx.font = 'bold 11px Arial';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.save();
      ctx.translate(labelColW / 2, rowTop + colH / 2);
      ctx.fillText(ws.label, 0, 0);
      ctx.restore();

      DOOR_TYPES.forEach((dt, ci) => {
        PANEL_VARIANTS.forEach((pv, vi) => {
          const cellX = labelColW + ci * (cellW + colGap);
          const cellY = rowTop + vi * (cellH + variantGap);
          const cx = cellX + cellW / 2;
          const cy = cellY + cellH / 2;

          // 셀 배경
          ctx.fillStyle = '#fff';
          ctx.fillRect(cellX, cellY, cellW, cellH);
          ctx.strokeStyle = '#ddd'; ctx.lineWidth = 1;
          ctx.strokeRect(cellX, cellY, cellW, cellH);

          // 패널 번호 라벨
          ctx.fillStyle = '#999'; ctx.font = '9px Arial';
          ctx.textAlign = 'left'; ctx.textBaseline = 'top';
          ctx.fillText(`P${pv.no}`, cellX + 2, cellY + 2);

          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(ws.angle);
          const pw = panelW + TD * 2;
          ctx.translate(-pw / 2, -PT / 2);
          drawCell(dt.type, pv);
          ctx.restore();
        });
      });

      rowTop += colH + rowGap;
    });

  }, []);

  return (
    <div style={{ padding: 20, background: '#fafafa', height: '100vh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      <h3 style={{ marginBottom: 8, flexShrink: 0 }}>문(Door) 타입 테스트 - 48종류</h3>
      <div style={{ overflow: 'auto', flex: 1 }}>
        <canvas ref={canvasRef} style={{ border: '1px solid #ddd', background: '#fff', display: 'block' }} />
      </div>
    </div>
  );
};

export default DoorTest;
